// src/pages/client-tabs/ClientPaymentsTab.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiCreditCard,
  FiFileText,
  FiUser,
  FiRefreshCcw,
  FiCalendar,
  FiHash,
} from 'react-icons/fi';

import api from '../../api/axios';

/* ----------------------------- helpers ----------------------------- */

const fmtMoney = (n, c = 'USD') =>
  (typeof n === 'number' ? n : Number(n || 0)).toLocaleString(undefined, {
    style: 'currency',
    currency: c || 'USD',
  });

const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

const resolveId = (row) => row?.paymentId || row?._id || row?.id;

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Disputed', value: 'disputed' },
  { label: 'Canceled', value: 'canceled' },
];

const METHOD_OPTIONS = [
  { label: 'Card', value: 'card' },
  { label: 'Cash', value: 'cash' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'E-transfer', value: 'e_transfer' },
  { label: 'Other', value: 'other' },
];

const SOURCE_OPTIONS = [
  { label: 'Manual', value: 'manual' },
  { label: 'Stripe', value: 'stripe' },
];

const STATUS_CHIP = {
  pending: 'bg-amber-100 text-amber-700 border border-amber-200',
  succeeded: 'bg-green-100 text-green-700 border border-green-200',
  failed: 'bg-rose-100 text-rose-700 border border-rose-200',
  refunded: 'bg-blue-100 text-blue-700 border border-blue-200',
  disputed: 'bg-orange-100 text-orange-700 border border-orange-200',
  canceled: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const SOURCE_CHIP = {
  manual: 'bg-slate-100 text-slate-800 border border-slate-200',
  stripe: 'bg-purple-100 text-purple-800 border border-purple-200',
};

const SORTABLE = [
  { id: 'paymentDate', label: 'Payment Date' },
  { id: 'createdAt', label: 'Created' },
  { id: 'amount', label: 'Amount' },
  { id: 'netAmount', label: 'Net Amount' },
];

/* ----------------------------- portal action menu ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate, // reserved for future destructive actions
  onClose,
  onViewPayment,
  onViewInvoice,
  onViewClient,
  onOpenInvoicePdf,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;

    const menuW = 240;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle({ top, left, origin });

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const paymentId = resolveId(row);
  const invoiceId = row?.invoiceId || row?.invoice?._id || row?.invoice?.id;
  const clientId = row?.clientId || row?.client?._id || row?.client?.id;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      {paymentId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewPayment(paymentId);
          }}
        >
          <FiHash /> View payment
        </button>
      )}

      {invoiceId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewInvoice(invoiceId);
          }}
        >
          <FiFileText /> View invoice
        </button>
      )}

      {invoiceId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={async () => {
            onClose();
            await onOpenInvoicePdf(invoiceId);
          }}
        >
          <FiFileText /> Open invoice PDF
        </button>
      )}

      {clientId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewClient(clientId);
          }}
        >
          <FiUser /> View client
        </button>
      )}

      {/* Reserved for future destructive actions (delete/refund) */}
      {/* 
      <div className="my-1 h-px bg-slate-100" />
      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${
          canMutate ? 'hover:bg-rose-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onDeletePayment(paymentId);
        }}
      >
        <FiTrash2 className="inline -mt-0.5 mr-1" /> Delete payment
      </button>
      */}
    </div>,
    document.body
  );
}

/* ----------------------------- main: client-scoped tab ----------------------------- */

const ClientPaymentsTab = ({
  payments = [],
  canEdit = false, // reserved for future (e.g., delete/refund)
  fetchPayments, // optional refetch callback when we add mutations
}) => {
  const navigate = useNavigate();

  // Query state (client-scoped, simplified)
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [method, setMethod] = useState('all');
  const [source, setSource] = useState('all');
  const [sortBy, setSortBy] = useState('paymentDate');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination (fixed size like invoices tab)
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [search, status, method, source, sortBy, sortDir]);

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
    setMethod('all');
    setSource('all');
    setSortBy('paymentDate');
    setSortDir('desc');
    setPage(1);
  };

  /* ----------------------------- filtering & sorting ----------------------------- */

  const filtered = useMemo(() => {
    let rows = Array.isArray(payments) ? payments.slice() : [];

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const num = String(r.paymentNumber || '').toLowerCase();
        const ref = String(r.reference || '').toLowerCase();
        const inv = String(
          r.invoiceNumber || r.invoice?.invoiceNumber || ''
        ).toLowerCase();
        return num.includes(q) || ref.includes(q) || inv.includes(q);
      });
    }

    if (status !== 'all') {
      rows = rows.filter((r) => String(r.status || '').toLowerCase() === status);
    }

    if (method !== 'all') {
      rows = rows.filter((r) => String(r.method || '').toLowerCase() === method);
    }

    if (source !== 'all') {
      rows = rows.filter((r) => String(r.source || '').toLowerCase() === source);
    }

    rows.sort((a, b) => {
      const av = a?.[sortBy];
      const bv = b?.[sortBy];

      let cmp = 0;
      if (av == null && bv != null) cmp = -1;
      else if (av != null && bv == null) cmp = 1;
      else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else if (!isNaN(Date.parse(av)) && !isNaN(Date.parse(bv)))
        cmp = new Date(av) - new Date(bv);
      else
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, {
          numeric: true,
        });

      return sortDir === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [payments, search, status, method, source, sortBy, sortDir]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / limit)),
    [filtered.length, limit]
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page, limit]);

  /* ----------------------------- actions ----------------------------- */

  const openInvoicePdf = useCallback(async (invoiceId) => {
    try {
      if (!invoiceId) throw new Error('Missing invoice id');

      const resp = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
        params: { download: 1, v: Date.now() },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();

      if (ct.includes('application/json')) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || '{}');
          throw new Error(json?.error || 'Server error while generating PDF');
        } catch {
          throw new Error(text || 'Server error while generating PDF');
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0)
        throw new Error('Empty PDF response');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(`PDF error: ${e?.message || 'Unable to download invoice PDF'}`);
    }
  }, []);

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  /* ----------------------------- render ----------------------------- */

  return (
    <div className="w-full">
      {/* Filters Bar (client-scoped) */}
      <div className="mb-4">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-3 overflow-x-auto">
          <div className="flex gap-2 items-center min-w-max">
            {/* Search */}
            <div className="relative w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search payment #, invoice # or reference…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Status */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                title="Filter by status"
              >
                <option value="all">All Statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Method */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setPage(1);
                }}
                title="Filter by method"
              >
                <option value="all">All Methods</option>
                {METHOD_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Source */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(1);
                }}
                title="Filter by source"
              >
                <option value="all">All Sources</option>
                {SOURCE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort by */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                title="Sort by"
              >
                {SORTABLE.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort direction */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setPage(1);
                }}
                title="Sort direction"
              >
                <option value="desc">Newest</option>
                <option value="asc">Oldest</option>
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Reset */}
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
              onClick={resetFilters}
              title="Reset filters"
            >
              <FiRefreshCcw /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="paymentDate" label="Payment Date" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="createdAt" label="Created" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Payment #</th>
                <th className="px-4 py-3 text-left font-bold">Invoice</th>
                <th className="px-4 py-3 text-left font-bold">Method</th>
                <th className="px-4 py-3 text-left font-bold">Source</th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="amount" label="Amount" />
                </th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="netAmount" label="Net" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-14">
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <FiCreditCard className="text-slate-400" />
                      </div>
                      <div className="text-slate-600 font-semibold">No payments found</div>
                      <div className="text-slate-500 text-sm">
                        This client has no recorded payments yet.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => {
                  const id = resolveId(row);
                  const invoiceId =
                    row?.invoiceId || row?.invoice?._id || row?.invoice?.id;
                  const clientId =
                    row?.clientId || row?.client?._id || row?.client?.id;

                  const invoiceNumber =
                    row?.invoiceNumber || row?.invoice?.invoiceNumber || '—';
                  const clientName =
                    row?.clientName ||
                    row?.client?.name ||
                    row?.client?.displayName ||
                    '—';

                  const currency = row.currency || 'USD';
                  const statusKey = String(row.status || '').toLowerCase();
                  const sourceKey = String(row.source || '').toLowerCase();

                  const methodLabel = row.method
                    ? {
                        card: 'Card',
                        cash: 'Cash',
                        cheque: 'Cheque',
                        bank_transfer: 'Bank Transfer',
                        e_transfer: 'E-transfer',
                        other: 'Other',
                      }[row.method] || row.method
                    : '—';

                  return (
                    <tr key={id || `${invoiceId}-${row.paymentDate}-${row.amount}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {fmtDateOnlyUTC(row.paymentDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {fmtDateOnlyUTC(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {id ? (
                          <button
                            className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1"
                            onClick={() => navigate(`/payments/${id}`)}
                            title="Open payment"
                          >
                            <FiHash className="text-slate-500" />
                            {row.paymentNumber || '—'}
                          </button>
                        ) : (
                          <span className="font-semibold text-slate-900">
                            {row.paymentNumber || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {invoiceId ? (
                          <button
                            className="font-medium text-blue-700 hover:underline"
                            onClick={() => navigate(`/invoices/${invoiceId}`)}
                            title="Open invoice"
                          >
                            {invoiceNumber}
                          </button>
                        ) : (
                          <span className="text-slate-700">{invoiceNumber}</span>
                        )}
                        {clientName && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <FiUser /> {clientName}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {methodLabel}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            SOURCE_CHIP[sourceKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.source
                            ? row.source.charAt(0).toUpperCase() + row.source.slice(1)
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[statusKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status
                            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.amount, currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {row.netAmount != null && row.netAmount >= 0
                          ? fmtMoney(row.netAmount, currency)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={
                              menuRow && resolveId(menuRow) === id ? 'true' : 'false'
                            }
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {pageRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No payments.</div>
          ) : (
            pageRows.map((row) => {
              const id = resolveId(row);
              const invoiceId =
                row?.invoiceId || row?.invoice?._id || row?.invoice?.id;
              const clientId =
                row?.clientId || row?.client?._id || row?.client?.id;

              const invoiceNumber =
                row?.invoiceNumber || row?.invoice?.invoiceNumber || '—';
              const clientName =
                row?.clientName ||
                row?.client?.name ||
                row?.client?.displayName ||
                '—';

              const currency = row.currency || 'USD';
              const statusKey = String(row.status || '').toLowerCase();
              const sourceKey = String(row.source || '').toLowerCase();

              const methodLabel = row.method
                ? {
                    card: 'Card',
                    cash: 'Cash',
                    cheque: 'Cheque',
                    bank_transfer: 'Bank Transfer',
                    e_transfer: 'E-transfer',
                    other: 'Other',
                  }[row.method] || row.method
                : '—';

              return (
                <div
                  key={id || `${invoiceId}-${row.paymentDate}-${row.amount}`}
                  className="p-4 hover:bg-blue-50/60 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1">
                        <FiCreditCard className="text-slate-500" />
                        {fmtMoney(row.amount, currency)}
                      </div>
                      <div className="text-sm text-slate-600 flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> {fmtDateOnlyUTC(row.paymentDate)}
                        </span>
                        {invoiceNumber && (
                          <span className="inline-flex items-center gap-1">
                            • #{invoiceNumber}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        {clientId ? (
                          <button
                            className="font-medium hover:underline"
                            onClick={() => navigate(`/clients/${clientId}`)}
                          >
                            {clientName}
                          </button>
                        ) : (
                          clientName
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        Method: {methodLabel}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[statusKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status
                            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
                            : '—'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            SOURCE_CHIP[sourceKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.source
                            ? row.source.charAt(0).toUpperCase() + row.source.slice(1)
                            : '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm">
                        {row.paymentNumber || '—'}
                      </div>
                      {id && (
                        <button
                          className="mt-1 text-xs text-blue-700 hover:underline"
                          onClick={() => navigate(`/payments/${id}`)}
                        >
                          View payment
                        </button>
                      )}
                      {invoiceId && (
                        <button
                          className="mt-1 text-xs text-blue-700 hover:underline block"
                          onClick={() => navigate(`/invoices/${invoiceId}`)}
                        >
                          View invoice
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {id && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/payments/${id}`)}
                      >
                        <FiHash className="inline -mt-0.5 mr-1" />
                        Payment
                      </button>
                    )}

                    {invoiceId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/invoices/${invoiceId}`)}
                      >
                        Invoice
                      </button>
                    )}

                    {invoiceId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={async () => {
                          await openInvoicePdf(invoiceId);
                        }}
                      >
                        <FiFileText className="inline -mt-0.5 mr-1" />
                        Invoice PDF
                      </button>
                    )}

                    {clientId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/clients/${clientId}`)}
                      >
                        Client
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{page}</span> of{' '}
            <span className="font-semibold">{totalPages}</span> •{' '}
            <span className="font-semibold">{filtered.length}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={canEdit}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onViewPayment={(paymentId) => navigate(`/payments/${paymentId}`)}
          onViewInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          onViewClient={(clientId) => navigate(`/clients/${clientId}`)}
          onOpenInvoicePdf={openInvoicePdf}
        />
      )}
    </div>
  );
};

export default ClientPaymentsTab;
